import React, { useCallback, useState } from "react";
import { useEffectAfterMount, useQuery } from "../../../util/Hooks";
import { useHistory } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import DateInput from "./date-input";
import TextInput from "./text-input";
import { Dropdown } from "../layout/Dropdown";
import {
  Button,
  DropdownButton,
  FormControl,
  InputGroup,
} from "react-bootstrap";
import { CheckIcon, DropdownIcon } from "../../../util/Icons";
import { Dropdown as DropdownMenu } from "react-bootstrap";

const FilterGroupContext = React.createContext();
const FilterContext = React.createContext();

export const FilterGroup = ({ defaultFilters, ...props }) => {
  const query = useQuery();
  const history = useHistory();
  const filtersQuery = query.get("filters");
  const [filters, setFilters] = useState(
    filtersQuery
      ? { ...defaultFilters, ...JSON.parse(filtersQuery) }
      : { ...defaultFilters }
  );

  useEffectAfterMount(() => {
    history.replace(
      `${history.location.pathname}?filters=${JSON.stringify(filters)}`
    );
  }, [filters]);

  const addFilter = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const addFilters = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const removeFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
  };

  return (
    <FilterGroupContext.Provider
      value={{ filters, addFilter, removeFilter, addFilters }}
    >
      {props.children}
    </FilterGroupContext.Provider>
  );
};

function useFilterGroupContext() {
  const context = React.useContext(FilterGroupContext);
  if (!context) {
    throw new Error(
      `FilterGroup compound components cannot be rendered outside the FilterGroup component`
    );
  }
  return context;
}

function useFilterContext() {
  const context = React.useContext(FilterContext);
  if (!context) {
    throw new Error(
      `FilterOptions component cannot be rendered outside a Filter component`
    );
  }
  return context;
}

function Filter({
  name,
  label,
  width = "160px",
  menuAnchorRight = false,
  ...props
}) {
  const { filters, addFilter, removeFilter } = useFilterGroupContext();

  const onSelect = useCallback(
    (selected) => {
      selected ? addFilter(name, selected) : removeFilter(name);
    },
    [addFilter, removeFilter, name]
  );

  return (
    <Dropdown>
      <Dropdown.Button>
        <Button variant={filters[name] ? "primary" : "dark"}>
          <span className="mr-2 font-medium">{label}</span>
          <DropdownIcon size={"14px"} />
        </Button>
      </Dropdown.Button>
      <Dropdown.Menu anchorRight={menuAnchorRight}>
        <div
          style={{
            width: width,
          }}
          className="rounded-lg bg-white shadow-sm border overflow-hidden"
        >
          <FilterContext.Provider
            value={{ activeItem: filters[name], select: onSelect }}
          >
            {props.children}
          </FilterContext.Provider>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function FilterOption({ value, ...props }) {
  const { activeItem, select } = useFilterContext();
  const isActive = activeItem === value;

  return (
    <div
      className={`p-3 text-hover-primary d-flex align-items-center cursor-pointer ${
        isActive ? "bg-light-primary text-primary" : ""
      }`}
      onClick={() => select(value)}
    >
      {isActive && <CheckIcon size={"14px"} />}
      <div className="ml-2 flex-fill">
        {props.children instanceof Function
          ? props.children({ active: isActive, value: value })
          : props.children}
      </div>
    </div>
  );
}

function FilterAmount({ name, className }) {
  const { filters, addFilters } = useFilterGroupContext();

  const [value, setValue] = useState(filters[name]);

  useEffectAfterMount(() => {
    addFilters({ [name]: value });
  }, [value]);

  return (
    <div className={className}>
      <Formik
        enableReinitialize
        initialValues={{
          value: value || 0,
        }}
        validationSchema={Yup.object({
          value: Yup.number()
            .required()
            .min(0),
        })}
        onSubmit={(values, { resetForm }) => {
          setValue(values?.value);
          resetForm();
        }}
      >
        {({ isValid, dirty }) => (
          <Form className={"d-flex"}>
            <TextInput name="value" type="number" />
            {dirty && (
              <button
                type="submit"
                disabled={!isValid}
                className={`btn btn-primary mt-auto`}
              >
                Update
              </button>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}

function FilterDate({ name, label, className }) {
  const { filters, addFilters } = useFilterGroupContext();

  const [date, setDate] = useState(filters[name]);

  useEffectAfterMount(() => {
    addFilters({ [name]: date });
  }, [date]);

  return (
    <div className={className}>
      <Formik
        initialValues={{
          date: date || "",
        }}
        validationSchema={Yup.object({
          date: Yup.date().max(new Date(), "Can't pick date later than today"),
        })}
        onSubmit={(values, { setSubmitting, setStatus }) => {
          setDate(values?.date);
          setStatus(false);
          setSubmitting(false);
        }}
      >
        {({ status, setStatus, isValid }) => (
          <Form className={"d-flex"}>
            <DateInput
              label={label}
              name="date"
              className="mr-4"
              max={new Date().toISOString().split("T")[0]}
              onChange={() => {
                setStatus(true);
              }}
            />
            {status && (
              <button
                type="submit"
                disabled={!isValid}
                className={`btn btn-primary mt-auto`}
              >
                Update
              </button>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}

function FilterDateRange({ className }) {
  const {
    filters: { startDate, endDate },
    addFilters,
  } = useFilterGroupContext();

  const [dateRange, setDateRange] = useState({
    start: startDate,
    end: endDate,
  });

  useEffectAfterMount(() => {
    addFilters({ startDate: dateRange.start, endDate: dateRange.end });
  }, [dateRange]);

  return (
    <div className={className}>
      <Formik
        initialValues={{
          startDate: startDate || "",
          endDate: endDate || "",
        }}
        validationSchema={Yup.object({
          startDate: Yup.date().max(
            Yup.ref("endDate"),
            "Can't pick date later than end date"
          ),
          endDate: Yup.date().max(
            new Date(),
            "Can't pick date later than today"
          ),
        })}
        onSubmit={(values, { setSubmitting, setStatus }) => {
          setDateRange({ start: values?.startDate, end: values?.endDate });
          setStatus(false);
          setSubmitting(false);
        }}
      >
        {({ status, setStatus, values, isValid }) => (
          <Form className={"d-flex"}>
            <DateInput
              label="From"
              name="startDate"
              className="mr-4"
              max={values?.endDate}
              onChange={() => {
                setStatus(true);
              }}
            />
            <DateInput
              label="To"
              name="endDate"
              className="mr-4"
              min={values?.startDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={() => {
                setStatus(true);
              }}
            />
            {status && (
              <button
                type="submit"
                disabled={!isValid}
                className={`btn btn-primary mt-auto`}
              >
                Update
              </button>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}

function FilterContent({ ...props }) {
  const { filters } = useFilterGroupContext();

  return (
    <div {...props}>
      {props.children instanceof Function
        ? props.children({ filters })
        : props.children}
    </div>
  );
}

FilterGroup.Filter = Filter;
FilterGroup.Option = FilterOption;
FilterGroup.Amount = FilterAmount;
FilterGroup.Date = FilterDate;
FilterGroup.DateRange = FilterDateRange;
FilterGroup.Content = FilterContent;
